import {TeamOutlined} from '@ant-design/icons';
import {useQuery} from '@tanstack/react-query';
import {Col, Modal, PageHeader, Row, Table, TablePaginationConfig} from 'antd';
import moment from 'moment';
import React, {CSSProperties, FC, useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {FetcherFunc} from '../../../src/FetcherFunc';
import {getI18nOptions} from '../../../src/Formy/FormyEnum';
import {useFormy, useFormyValue} from '../../../src/Formy/hooks';
import {Policy, RequestStatus} from '../../../src/models/interfaces';
import {Request} from '../../../src/models/interfaces';
import {PostgrestQuery, getPostgrestQueryParams} from '../../../src/util/postgrest-query';
import {PD} from '../../../src/util/types';
import {FormyDatePicker} from '../Formy/FormyDatePicker';
import {FormyEnum} from '../Formy/FormyEnum';
import {Label} from '../Formy/FormyLabel';
import {FormyTextStr} from '../Formy/FormyText';
import {useApis} from '../apis/ApisContext';

const contentStyle = {cursor: 'pointer', color: '#1890ff'};
const detailsStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'monospace',
  fontSize: '13px',
  backgroundColor: '#f5f5f5',
  color: '#333333',
  padding: '16px',
  borderRadius: '4px',
  maxHeight: '70vh',
  overflow: 'auto',
};
const marginBottom20 = {
  marginBottom: '20px',
};
const marginLeft10 = {marginLeft: '10px'};

export const SupportedBrazilRequestType = ['brazil-subsidy-creation', 'brazil-subsidy-policy-creation'] as const;
export type SupportedBrazilRequestType = (typeof SupportedBrazilRequestType)[number];

export const SupportedBrazilRequestStatus = ['new', 'ok', 'error'] as const;
export type SupportedBrazilRequestStatus = (typeof SupportedBrazilRequestStatus)[number];

interface SubsidiesFilter {
  insured_external_id: null | string;
  policy_number: null | string;
  date_from: null | string;
  date_to: null | string;
  request_type: null | SupportedBrazilRequestType;
  status: null | SupportedBrazilRequestStatus;
}

// User selected date from and date to are local and the date values in the database are in UTC, so we must convert
// the dates to start of the day in UTC.
function getRequestsQueryParams(
  dateFrom: null | undefined | string,
  dateTo: null | undefined | string,
  policyIds: string[],
  requestType: null | undefined | SupportedBrazilRequestType,
  status: null | undefined | SupportedBrazilRequestStatus,
) {
  const params: PostgrestQuery[] = [
    {column: 'request_type', operator: 'in', value: Array.from(SupportedBrazilRequestType)},
  ];
  if (dateFrom) {
    params.push({
      column: 'updated_at',
      operator: 'gte',
      value: moment(dateFrom, 'YYYY-MM-DD').startOf('day').toISOString(),
    });
  }
  if (dateTo) {
    params.push({
      column: 'updated_at',
      operator: 'lte',
      value: moment(dateTo, 'YYYY-MM-DD').endOf('day').toISOString(),
    });
  }
  if (policyIds.length) {
    params.push({
      column: `request->>policy_id`,
      operator: 'in',
      value: policyIds,
    });
  }
  if (requestType) {
    params.push({
      column: 'request_type',
      operator: 'eq',
      value: requestType,
    });
  }
  if (status) {
    params.push({
      column: 'status',
      operator: 'eq',
      value: status,
    });
  }
  return getPostgrestQueryParams({and: params});
}

const fetchSubsidyRequests = async (
  authedFetcher: FetcherFunc,
  pagination: {current: number; pageSize: number; total: number; showSizeChanger: boolean; pageSizeOptions: string[]},
  filters: {
    policyNumber: null | undefined | string;
    insuredExternalId: null | undefined | string;
    dateFrom: null | undefined | string;
    dateTo: null | undefined | string;
    requestType: null | undefined | SupportedBrazilRequestType;
    status: null | undefined | SupportedBrazilRequestStatus;
  },
): Promise<{page: Request[]; total: number}> => {
  const policyConstraints: PostgrestQuery[] = [];
  if (filters.policyNumber) {
    policyConstraints.push({column: 'policy_number', operator: 'eq', value: filters.policyNumber});
  }
  if (filters.insuredExternalId) {
    policyConstraints.push({
      column: 'custom_columns->insured->>external_id',
      operator: 'eq',
      value: filters.insuredExternalId,
    });
  }
  const policyIds: string[] = [];
  if (policyConstraints.length) {
    const policies: Policy[] = await authedFetcher({
      method: 'GET',
      path: 'api/policy',
      params: [...getPostgrestQueryParams({and: policyConstraints}), ['select', 'policy_id']],
    });
    policyIds.push(...policies.map(policy => policy.policy_id));
    if (!policyIds.length) {
      return {page: [], total: 0};
    }
  }
  const queryParams = getRequestsQueryParams(
    filters.dateFrom,
    filters.dateTo,
    policyIds,
    filters.requestType,
    filters.status,
  );
  const countResp = await authedFetcher({
    method: 'GET',
    path: 'api/request',
    params: [...queryParams, ['select', 'count']],
  });

  const data = await authedFetcher({
    method: 'GET',
    path: 'api/request',
    params: [
      ...queryParams,
      ['order', 'updated_at.desc'],
      // TODO(kristjan): implement keyset pagination using updated_at and request_id.
      ['limit', pagination.pageSize.toString()],
      ['offset', ((pagination.current - 1) * pagination.pageSize).toString()],
    ],
  });
  return {page: data, total: countResp[0].count};
};

export function isValidRequest(x: unknown): x is PD<string> {
  return x !== null && typeof x == 'object';
}

export const BrazilSubsidyRequests: FC = () => {
  const {t, authedFetcher} = useApis();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
  });
  const formy = useFormy<SubsidiesFilter>(
    'new',
    async () => {
      return {
        insured_external_id: null,
        policy_number: null,
        date_from: null,
        date_to: null,
        request_type: null,
        status: null,
      };
    },
    t,
    async () => {},
    () => {
      return {};
    },
    null,
  );
  const dateFrom = useFormyValue(formy, 'date_from');
  const dateTo = useFormyValue(formy, 'date_to');
  const status = useFormyValue(formy, 'status');
  const requestType = useFormyValue(formy, 'request_type');
  const insuredExternalId = useFormyValue(formy, 'insured_external_id');
  const policyNumber = useFormyValue(formy, 'policy_number');
  const filters = {
    policyNumber,
    insuredExternalId,
    dateFrom,
    dateTo,
    requestType,
    status,
  };
  const {data, isLoading} = useQuery(
    ['subsidy-requests', pagination, filters],
    () => fetchSubsidyRequests(authedFetcher, pagination, filters),
    {
      keepPreviousData: true,
    },
  );
  const handlePaginationChange = (newPagination: TablePaginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    }));
  };
  const handleDetailsClick = useCallback(
    (content: string) => {
      Modal.info({
        title: t('Payload'),
        content: <pre style={detailsStyle}>{content}</pre>,
        width: 1000,
        onOk() {},
      });
    },
    [t],
  );
  const columns = useMemo(
    () => [
      {
        title: t('Proposal') + ' / ' + t('Policy'),
        dataIndex: 'request',
        key: 'policy_id',
        render: (value: Request['request']) => {
          if (!value.policy_id) {
            return null;
          }
          return (
            <Link to={'/edit/policy?policy_id=' + value.policy_id} target={'_blank'}>
              {t('Open')}
            </Link>
          );
        },
      },
      {
        title: t('Type'),
        dataIndex: 'request_type',
        key: 'request_type',
        render: (value: SupportedBrazilRequestType) => {
          return <span>{t(value)}</span>;
        },
      },
      {
        title: t('AddedOn'),
        dataIndex: 'added_on',
        key: 'added_on',
        render: (value: string) => {
          return <span>{new Date(value).toLocaleString()}</span>;
        },
      },
      {
        title: t('LastChanged'),
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: (value: string) => {
          return <span>{new Date(value).toLocaleString()}</span>;
        },
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        key: 'status',
        render: (value: RequestStatus) => {
          return <span>{t(value)}</span>;
        },
      },
      {
        title: t('Message'),
        dataIndex: 'response',
        key: 'messages',
        render: (response: unknown) => {
          if (!isValidRequest(response) || !response.messages?.length) {
            return null;
          }
          return <span>{response.messages}</span>;
        },
      },
      {
        title: t('Payload'),
        dataIndex: 'response',
        key: 'payload',
        render: (response: unknown) => {
          if (!isValidRequest(response) || !response.payload) {
            return null;
          }
          return (
            <span onClick={() => handleDetailsClick(JSON.stringify(response.payload, null, 2))} style={contentStyle}>
              {t('View')}
            </span>
          );
        },
      },
      {
        title: t('Response'),
        dataIndex: 'response',
        key: 'response',
        render: (response: unknown) => {
          if (!isValidRequest(response) || !response.result) {
            return null;
          }
          return (
            <span onClick={() => handleDetailsClick(JSON.stringify(response.result, null, 2))} style={contentStyle}>
              {t('View')}
            </span>
          );
        },
      },
    ],
    [t, handleDetailsClick],
  );
  const paginationMemo = useMemo(() => ({...pagination, total: data?.total ?? 0}), [pagination, data]);
  return (
    <div className="admin-page">
      <Row>
        <Col span={24}>
          <PageHeader title={t('SubsidyRequests')} avatar={{icon: <TeamOutlined />}} />
        </Col>
      </Row>
      {formy && (
        <>
          <Row>
            <Col span={4}>
              <FormyTextStr formy={formy} field={'policy_number'} label={'PolicyNumber'} />
            </Col>
            <Col span={4} style={marginLeft10}>
              <FormyDatePicker formy={formy} field="date_from" label={'StartDate'} />
            </Col>
            <Col span={4} style={marginLeft10}>
              <FormyDatePicker formy={formy} field="date_to" label={'EndDate'} />
            </Col>
          </Row>
          <Row style={marginBottom20}>
            <Col span={4}>
              <FormyTextStr formy={formy} field={'insured_external_id'} label={'DocumentNumber'} />
            </Col>
            <Col span={4} style={marginLeft10}>
              <Label>{t('Type')}</Label>
              <FormyEnum
                formy={formy}
                options={getI18nOptions(t, Array.from(SupportedBrazilRequestType))}
                field="request_type"
                selectMsg={'Select'}
              />
            </Col>
            <Col span={4} style={marginLeft10}>
              <Label>{t('Status')}</Label>
              <FormyEnum
                formy={formy}
                options={getI18nOptions(t, ['new', 'ok', 'error'])}
                field="status"
                selectMsg={'Select'}
              />
            </Col>
          </Row>
        </>
      )}
      <Row>
        <Col span={20}>
          <Table
            dataSource={data?.page}
            columns={columns}
            rowKey={(row: Request) => row.request_id}
            pagination={paginationMemo}
            onChange={handlePaginationChange}
            loading={isLoading}
          />
        </Col>
      </Row>
    </div>
  );
};
